import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { abyssColors } from '@/theme/colors';
import {
  selectedRiskAssessmentFilters,
  riskAssessmentAssemblyTemplate,
  selectedAssemblyId,
  selectedStructureRiskAssessment,
  structureTemplate as structureTemplateState,
} from '@/components/Analysis/state';
import {
  useGetAssembliesByRiskAssessmentFiltersLazyQuery,
  useAllCommentsForRiskAssessmentLazyQuery,
  RiskFieldsTypeEnum,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { formatEnumForDisplay, getEquipmentColumnsFromAssemblyTemplate } from '@/utils';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import {
  ColumnTitleTypography,
  TableHeadCell,
} from '@/components/Analysis/Insights/EquipmentTable/styles';
import { TransitionPeriodGraph } from './TransitionPeriod/TransitionPeriodGraph';

interface TableResult {
  title: string;
  value: string;
  comment: string;
}

export const RiskAssessmentDrawerTable = ({ selectedTab }: { selectedTab: RiskFieldsTypeEnum }) => {
  const structureId = useGetStructureId();
  const assemblyId = useAtomValue(selectedAssemblyId);
  const structure = useAtomValue(selectedStructureRiskAssessment);
  const structureTemplate = useAtomValue(structureTemplateState);
  const template = useAtomValue(riskAssessmentAssemblyTemplate);
  const selectedFilters = useAtomValue(selectedRiskAssessmentFilters);
  const [tableData, setTableData] = useState<TableResult[]>([]);

  const shipsAtBerth = useMemo(() => {
    const x = structureTemplate?.fields.find(
      (f) => f.name === '(N) Number of ships annually accepted at the berth'
    );
    return structure?.templateData?.find((td) => td.id === x?.id)?.value;
  }, [structure?.templateData, structureTemplate?.fields]);

  const [fetchFilteredAssemblies, { loading: isLoading }] =
    useGetAssembliesByRiskAssessmentFiltersLazyQuery();
  const [fetchRiskAssemblyComments, { loading: isCommentsLoading }] =
    useAllCommentsForRiskAssessmentLazyQuery();

  const { showSnackBar } = useSnackBarMessage({
    variant: 'filled',
  });

  const cellStyles = {
    backgroundColor: 'white',
    border: '1px solid rgba(224, 224, 224, 224)',
    whiteSpace: 'nowrap',
    padding: '8px',
  } as React.CSSProperties;

  const filteredRiskFieldKeys = useMemo(() => {
    if (template?.riskFields) {
      return template.riskFields
        .filter((riskField) => riskField.type.includes(selectedTab))
        .map((riskField) => riskField.fieldKey);
    }
    return [];
  }, [selectedTab, template?.riskFields]);

  const fetchAssemblyData = useCallback(async () => {
    try {
      const { data } = await fetchFilteredAssemblies({
        variables: {
          input: {
            structureId,
            templateId: template?.id!,
            selectedFilters,
            assemblyId,
          },
        },
      });
      return data?.getAssembliesByRiskAssessmentFilters;
    } catch (error) {
      console.error('Error fetching assemblies:', error);
      showSnackBar('Error fetching assemblies:', 'error');
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFilteredAssemblies, structureId, template?.id, selectedFilters, assemblyId]);

  // fetching columns that are included in filteredKeys and also remove the non-required values that have display in risk as true (structure description, content type etc)
  const fetchColumns = useCallback(() => {
    if (filteredRiskFieldKeys && template) {
      const columnsFromTemplate = getEquipmentColumnsFromAssemblyTemplate(template);
      return columnsFromTemplate.filter(
        (field) =>
          !field.displayInRisk && filteredRiskFieldKeys.includes(field.templateFieldKey ?? '')
      );
    }
    return [];
  }, [filteredRiskFieldKeys, template]);

  const fetchComments = useCallback(async () => {
    try {
      const { data } = await fetchRiskAssemblyComments({
        variables: {
          input: {
            structureId,
            collectionName: 'assembly',
            referenceId: assemblyId ?? '',
            type: selectedTab,
          },
        },
      });
      return data?.allComments;
    } catch (error) {
      console.error('Error fetching comments:', error);
      showSnackBar('Error fetching comments:', 'error');
      return [];
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assemblyId, fetchRiskAssemblyComments, structureId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [assemblyData, columns, comments] = await Promise.all([
          fetchAssemblyData(),
          Promise.resolve(fetchColumns()),
          fetchComments(),
        ]);
        const result: TableResult[] = [];
        if (assemblyData) {
          for (const item of assemblyData) {
            for (const { templateFieldKey, columnName } of columns) {
              if (templateFieldKey) {
                const value = item.templateFieldData[templateFieldKey];
                const selectedComment = comments?.find(
                  (comment) => comment.key === templateFieldKey
                );
                result.push({
                  title: columnName,
                  value,
                  comment: selectedComment?.text ?? 'N/A',
                });
              }
            }
          }
        }

        setTableData(result);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [fetchAssemblyData, fetchColumns, fetchComments, selectedTab, shipsAtBerth]);

  return (
    <Stack>
      {!isLoading && (
        <Stack gap={5}>
          <TableContainer
            component={Paper}
            sx={{ position: 'relative', overflow: 'auto', maxHeight: '700px' }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ p: 1, width: '30%' }}>
                    <TableHeadCell>
                      <ColumnTitleTypography>
                        {formatEnumForDisplay(selectedTab)} values
                      </ColumnTitleTypography>
                    </TableHeadCell>
                  </TableCell>

                  <TableCell sx={{ p: 1, width: '30%' }}>
                    <TableHeadCell>
                      <ColumnTitleTypography>Value</ColumnTitleTypography>
                    </TableHeadCell>
                  </TableCell>

                  {selectedTab !== RiskFieldsTypeEnum.Review && (
                    <TableCell sx={{ p: 1, width: '40%' }}>
                      <TableHeadCell>
                        <ColumnTitleTypography>Comments</ColumnTitleTypography>
                      </TableHeadCell>
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              {tableData.length > 0 && (
                <TableBody sx={{ borderRadius: '0.5rem' }}>
                  {tableData.map((value, index) =>
                    value.value ? (
                      <TableRow key={`${value.title}-${index}`}>
                        <TableCell style={cellStyles}>
                          <Stack>{value.title}</Stack>
                        </TableCell>
                        <TableCell style={cellStyles}>
                          <Stack>{value.value}</Stack>
                        </TableCell>
                        {selectedTab !== RiskFieldsTypeEnum.Review && (
                          <TableCell
                            style={{
                              ...cellStyles,
                              color: value.comment === 'N/A' ? abyssColors.primary[300] : 'inherit',
                            }}
                          >
                            <Stack>{value.comment}</Stack>
                          </TableCell>
                        )}
                      </TableRow>
                    ) : (
                      <></>
                    )
                  )}
                </TableBody>
              )}
            </Table>
          </TableContainer>
          {selectedTab === RiskFieldsTypeEnum.TransitionPeriod && (
            <TransitionPeriodGraph data={tableData} />
          )}
        </Stack>
      )}
      {isLoading || isCommentsLoading ? (
        <Stack
          sx={{
            py: 20,
            borderRadius: 2,
          }}
        >
          <img
            src="/assets/abyss_fabric_mini_logo_black.svg"
            style={{ height: '6.4rem' }}
            alt="fabric-logo"
          />
        </Stack>
      ) : (
        tableData.length === 0 && (
          <Stack
            sx={{
              py: 20,
              alignItems: 'center',
              borderRadius: 2,
            }}
          >
            <InfoOutlinedIcon sx={{ color: abyssColors.primary[400], fontSize: '6rem' }} />
            <Typography
              variant="h6"
              fontWeight={500}
              sx={{
                color: abyssColors.primary[400],
                whiteSpace: 'nowrap',
                mt: 1,
              }}
            >
              No data found
            </Typography>
          </Stack>
        )
      )}
    </Stack>
  );
};
